// apps/web/hooks/useDeviceType.ts
'use client';

import { useEffect, useState } from 'react';
import type { DeviceType } from '@/lib/device-type';

interface DeviceTypeResult {
  deviceType: DeviceType;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  width: number;
}

// Breakpoints from your design system (apps/web/styles/tokens/spacing.css)
const BREAKPOINTS = {
  tablet: 768, // --breakpoint-md
  desktop: 1024, // --breakpoint-lg
} as const;

function defaultWidthForDeviceType(deviceType: DeviceType): number {
  switch (deviceType) {
    case 'mobile':
      return BREAKPOINTS.tablet - 1;
    case 'tablet':
      return BREAKPOINTS.desktop - 1;
    case 'desktop':
    default:
      return BREAKPOINTS.desktop;
  }
}

export function useDeviceType(initialDeviceType: DeviceType = 'desktop'): DeviceTypeResult {
  const [deviceState, setDeviceState] = useState<DeviceTypeResult>({
    deviceType: initialDeviceType,
    isMobile: initialDeviceType === 'mobile',
    isTablet: initialDeviceType === 'tablet',
    isDesktop: initialDeviceType === 'desktop',
    width: defaultWidthForDeviceType(initialDeviceType),
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const deviceType = getDeviceType(width);

      setDeviceState({
        deviceType,
        isMobile: deviceType === 'mobile',
        isTablet: deviceType === 'tablet',
        isDesktop: deviceType === 'desktop',
        width,
      });
    };

    // Set initial state
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return deviceState;
}

function getDeviceType(width: number): DeviceType {
  if (width < BREAKPOINTS.tablet) {
    return 'mobile';
  } else if (width < BREAKPOINTS.desktop) {
    return 'tablet';
  } else {
    return 'desktop';
  }
}

// Additional utility hooks for specific use cases
export function useIsMobile(): boolean {
  const { isMobile } = useDeviceType();
  return isMobile;
}

export function useIsTablet(): boolean {
  const { isTablet } = useDeviceType();
  return isTablet;
}

export function useIsDesktop(): boolean {
  const { isDesktop } = useDeviceType();
  return isDesktop;
}

// Hook for responsive values based on device type
export function useResponsiveValue<T>(values: { mobile: T; tablet?: T; desktop: T }): T {
  const { deviceType } = useDeviceType();

  switch (deviceType) {
    case 'mobile':
      return values.mobile;
    case 'tablet':
      return values.tablet ?? values.desktop;
    case 'desktop':
      return values.desktop;
    default:
      return values.desktop;
  }
}
